import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  const articles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">分类未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您访问的分类不存在。</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium rounded-full transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Schema.org */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": `${category.name} - 爱情岛独家`,
        "description": category.description,
        "url": `https://aiqingdao.com/category/${category.slug}`,
        "isPartOf": {
          "@type": "WebSite",
          "name": "爱情岛独家",
          "url": "https://aiqingdao.com"
        },
        "about": {
          "@type": "Thing",
          "name": category.name
        }
      }) }} />

      {/* Breadcrumb */}
      <div className="pt-20 bg-background-50 border-b border-background-200/60">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/category/exclusive" className="hover:text-primary-600 transition-colors">内容分类</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium">{category.name}</span>
          </nav>
        </div>
      </div>

      {/* Category Header */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary-100">
              <i className={`${category.icon} text-2xl text-primary-600`}></i>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {category.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
            </div>
          </div>
          <p className="text-sm text-foreground-500">
            共 <strong className="text-foreground-800">{category.articleCount}</strong> 篇内容
          </p>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="sticky top-16 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-1 py-1 overflow-x-auto flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Articles List */}
      <section className="py-10 md:py-14 bg-background-100 min-h-[50vh]">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          {articles.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full aspect-[16/10] overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 text-xs font-medium bg-background-50/90 backdrop-blur-sm text-foreground-700 rounded-full">
                        {article.tags[0]}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <h2 className="text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-2 line-clamp-2 leading-snug">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-500 mb-3 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>{article.author}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i> {article.readTime}分钟
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}